import React, { useMemo } from 'react'
import { defer } from 'lodash'
import { Beta } from '@mc/design-system'
import styles from './GlobalCarousel.module.scss'
import { GlobalCarouselTile } from './GlobalCarouselTile'
import { TileSkeleton } from './TileSkeleton'
import { VideoModalContextProvider } from './VideoContext'
import {
  trackCarouselClickNext,
  trackCarouselClickPrev,
  trackTileClick,
} from './events'
import { prepareTiles } from './tileData'

const PRIORITIZED_TILES = 5

export type GlobalCarouselProps = {
  carouselSlug: string // something to describe this carousel specifically e.g. "food-and-drink"
  location: string // something to describe this carousel generally e.g. "quiz-results"
  tiles: unknown[]
  title?: React.ReactNode
  loading?: boolean
  openNewPage?: boolean
  trackProps?: Record<string, string | number | undefined>
} & Partial<Beta.CarouselModernProps>

export const GlobalCarousel = ({
  carouselSlug,
  loading,
  tiles: originalTiles,
  title,
  location,
  openNewPage,
  trackProps,
  ...carouselProps
}: GlobalCarouselProps) => {
  const tiles = useMemo(() => prepareTiles(originalTiles), [originalTiles])

  return (
    <VideoModalContextProvider>
      <Beta.CarouselModern
        title={title}
        srLabel={carouselSlug}
        className={styles.carousel}
        arrowCls='d-none d-sm-block'
        pagesCls='d-none d-sm-block'
        onClickNext={() => trackCarouselClickNext({ carouselSlug, location })}
        onClickPrev={() => trackCarouselClickPrev({ carouselSlug, location })}
        overflow
        {...carouselProps}
      >
        {loading
          ? [1, 2, 3, 4, 5].map((i) => (
              <div key={i} className={styles.tile}>
                <TileSkeleton />
              </div>
            ))
          : tiles.map((tile, idx) => (
              <div
                key={tile.slug}
                className={styles.tile}
                data-testid={`tile-${tile.slug}`}
              >
                <GlobalCarouselTile
                  tile={tile}
                  carouselSlug={carouselSlug}
                  location={location}
                  onClick={() =>
                    defer(() => {
                      trackTileClick({
                        classTitle: tile.title,
                        carouselSlug,
                        location,
                        link: tile.link,
                        ...trackProps,
                      })
                    })
                  }
                  isPrioritized={idx < PRIORITIZED_TILES}
                  openNewPage={openNewPage}
                />
              </div>
            ))}
      </Beta.CarouselModern>
    </VideoModalContextProvider>
  )
}
